import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Linking,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from "react-native";
import { useReferenceWindowDimensions } from "../utils/referenceViewport";
import { SafeAreaView } from "react-native-safe-area-context";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowRight, ArrowUp, KeyRound, LogOut, Plus } from "lucide-react-native";
import { RootStackParamList } from "../navigation/types";
import { useSession } from "../session/SessionProvider";
import { MatureTopNav, useBottomNavInset } from "../components/MatureTopNav";
import { FocusablePressable } from "../components/FocusablePressable";
import {
  defaultHomeConfig,
  NativeHomeConfig,
  NativeHomeSection,
  readHomeConfig,
  resetHomeConfig,
  saveHomeConfig,
} from "../storage/homeConfig";
import { buildHomeUserViewsSourceKey, mergeHomeConfigUserViews, orderHomeUserViews } from "../storage/homeUserViews";
import { defaultPreferences, MediaSegmentAction, MediaSegmentType, NativePreferences, readPreferences, savePreferences } from "../storage/preferences";
import { REMUX_SERVER_URL } from "../storage/credentials";
import { colors } from "../theme/tokens";
import { changeOwnPassword, getDiscordIdentities, getPlaybackDisplayPreferences, getUserConfiguration, getUserViews, initiateDiscordLink, JellyfinUserConfiguration, unlinkDiscord, updatePlaybackDisplayPreferences, updateUserConfiguration } from "../api/jellyfin";
import { setWebTelevisionMode } from "../utils/platformCapabilities";
import { productProfile } from "../productProfile";

type Props = NativeStackScreenProps<RootStackParamList, "Settings">;
type RouteResult = { label: string; host: string; latencyMs: number | null };
type SettingsCategory = "Account" | "Playback" | "Player Controls" | "Home" | "Appearance" | "Libraries" | "Connection & Advanced";

const SETTINGS_CATEGORIES: SettingsCategory[] = [
  "Account",
  "Playback",
  "Player Controls",
  "Home",
  "Appearance",
  "Libraries",
  "Connection & Advanced",
];

const ROUTE_CANDIDATES = [
  { label: "Cloudflare", url: "https://jellyfin.obnoxious.lol" },
  { label: "Direct", url: "https://jf.obnoxious.lol" },
] as const;

const SECTION_OPTIONS = [
  ["continueWatchingNextUp", "Continue Watching + Next Up"],
  ["continueWatching", "Continue Watching"],
  ["nextUp", "Next Up"],
  ["recentlyAdded", "Recently Added"],
  ["serverLibraries", "Your Libraries"],
  ["recommended", "Recommended"],
  ["genres", "Genres"],
  ["randomGenre", "Random Genre"],
  ["becauseYouWatched", "Because You Watched"],
  ["streamingServices", "Streaming Services"],
] as const;

const MEDIA_SEGMENT_TYPES: ReadonlyArray<readonly [MediaSegmentType, string]> = [
  ["Intro", "Intros"],
  ["Preview", "Previews"],
  ["Recap", "Recaps"],
  ["Commercial", "Commercials"],
  ["Outro", "Credits"],
];
const MEDIA_SEGMENT_ACTION_OPTIONS: ReadonlyArray<readonly [MediaSegmentAction, string]> = [
  ["None", "Off"],
  ["AskToSkip", "Show prompt"],
  ["Skip", "Always skip"],
];
const SEEK_LENGTH_OPTIONS: ReadonlyArray<readonly [string, string]> = [5, 10, 15, 20, 25, 30].map((seconds) => [String(seconds), `${seconds} seconds`] as const);

const PLAYER_CONTROL_GROUPS: Array<{ title: string; controls: Array<readonly [string, string]> }> = [
  { title: "Click Actions", controls: [["clickToPlayPause", "Click to play/pause"], ["doubleClickFullscreen", "Double-click fullscreen"]] },
  { title: "Overlays", controls: [["playbackPulse", "Play animation"], ["pauseOverlay", "Paused details overlay"], ["sourceFallbackStatus", "Source recovery status"], ["automaticDecisionToasts", "Automatic choice toasts"]] },
  { title: "Top Bar", controls: [["topBackButton", "Back button"], ["topTitle", "Title"], ["topFinishTime", "Finishes at time"]] },
  { title: "Timeline", controls: [["progressBar", "Progress bar"], ["trickplayPreview", "Hover preview"], ["duration", "Duration"]] },
  { title: "Main Buttons", controls: [["playPause", "Play/Pause"], ["previousEpisode", "Previous episode"], ["nextEpisode", "Next episode"], ["seekBackward", "Rewind"], ["seekForward", "Forward"], ["mute", "Mute"], ["volumeSlider", "Volume slider"], ["inlineTitle", "Inline title"]] },
  { title: "Menus", controls: [["stats", "Playback stats"], ["episodes", "Episodes browser"], ["versions", "Version picker"], ["playbackOptions", "Playback options menu"], ["audioSubtitles", "Audio/Subtitles menu"], ["audioTrackList", "Audio track list"], ["subtitleTrackList", "Subtitle track list"], ["preferredAudioLanguage", "Preferred audio selector"], ["preferredSubtitleLanguage", "Preferred subtitle selector"]] },
  { title: "Right Side Buttons", controls: [["pictureInPicture", "Picture-in-picture"], ["fullscreen", "Fullscreen"]] },
  { title: "Prompts", controls: [["skipIntro", "Skip intro"], ["skipCredits", "Skip credits"], ["nextEpisodePrompt", "Next episode prompt"]] },
];

function hostName(url: string) {
  return url.replace(/^https?:\/\//, "").replace(/\/.*$/, "");
}

function ActionButton({ label, danger, onPress, children }: { label: string; danger?: boolean; onPress: () => void; children: React.ReactNode }) {
  return (
    <FocusablePressable
      accessibilityLabel={label}
      onPress={onPress}
      style={(focused, pressed, hovered) => [
        styles.iconButton,
        danger && styles.iconButtonDanger,
        (focused || hovered) && styles.iconButtonActive,
        pressed && styles.pressed,
      ]}
    >
      {children}
    </FocusablePressable>
  );
}

function Toggle({ value, onChange }: { value: boolean; onChange: (value: boolean) => void }) {
  return (
    <FocusablePressable
      accessibilityRole="switch"
      accessibilityState={{ checked: value }}
      onPress={() => onChange(!value)}
      style={(focused, pressed, hovered) => [styles.toggle, value && styles.toggleOn, (focused || hovered) && styles.toggleActive, pressed && styles.pressed]}
    >
      <View style={[styles.toggleKnob, value && styles.toggleKnobOn]} />
    </FocusablePressable>
  );
}

function StatusPill({ label, value, tone, compact }: { label: string; value: string; tone?: "brand" | "success"; compact?: boolean }) {
  return (
    <View style={[styles.statusPill, compact && styles.statusPillCompact, tone === "brand" && styles.statusBrand, tone === "success" && styles.statusSuccess]}>
      <Text style={styles.statusLabel}>{label.toUpperCase()}</Text>
      <Text numberOfLines={1} style={styles.statusValue}>{value}</Text>
    </View>
  );
}

function Panel({ eyebrow, title, description, children }: { eyebrow: string; title: string; description: string; children: React.ReactNode }) {
  return (
    <View style={styles.panel}>
      <Text style={styles.panelEyebrow}>{eyebrow.toUpperCase()}</Text>
      <Text style={styles.panelTitle}>{title}</Text>
      <Text style={styles.panelDescription}>{description}</Text>
      <View style={styles.panelBody}>{children}</View>
    </View>
  );
}

function SectionTypeSelect({
  value,
  expanded,
  onToggle,
  onChange,
  accessibilityLabel,
}: {
  value: string;
  expanded: boolean;
  onToggle: () => void;
  onChange: (value: string) => void;
  accessibilityLabel: string;
}) {
  const currentLabel = SECTION_OPTIONS.find(([type]) => type === value)?.[1] ?? value;
  return (
    <View style={[styles.selectWrap, expanded && styles.selectWrapExpanded]}>
      <FocusablePressable
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        accessibilityState={{ expanded }}
        onPress={onToggle}
        style={(focused, pressed, hovered) => [styles.selectButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}
      >
        <Text numberOfLines={1} style={styles.selectText}>{currentLabel}</Text>
        <Text style={[styles.selectChevron, expanded && styles.selectChevronOpen]}>⌄</Text>
      </FocusablePressable>
      {expanded ? (
        <View accessibilityRole="menu" style={styles.selectMenu}>
          {SECTION_OPTIONS.map(([type, label]) => {
            const selected = type === value;
            return (
              <FocusablePressable
                key={type}
                accessibilityLabel={label}
                accessibilityRole="menuitem"
                accessibilityState={{ selected }}
                onPress={() => onChange(type)}
                style={(focused, pressed, hovered) => [
                  styles.selectOption,
                  selected && styles.selectOptionSelected,
                  (focused || hovered) && styles.selectOptionActive,
                  pressed && styles.pressed,
                ]}
              >
                <Text style={[styles.selectOptionText, selected && styles.selectOptionTextSelected]}>{label}</Text>
                {selected ? <Text style={styles.selectCheck}>✓</Text> : null}
              </FocusablePressable>
            );
          })}
        </View>
      ) : null}
    </View>
  );
}

function ChoiceSelect<T extends string>({
  value,
  options,
  expanded,
  onToggle,
  onChange,
  accessibilityLabel,
}: {
  value: T;
  options: ReadonlyArray<readonly [T, string]>;
  expanded: boolean;
  onToggle: () => void;
  onChange: (value: T) => void;
  accessibilityLabel: string;
}) {
  const currentLabel = options.find(([option]) => option === value)?.[1] ?? value;
  return (
    <View style={[styles.selectWrap, expanded && styles.selectWrapExpanded]}>
      <FocusablePressable accessibilityLabel={accessibilityLabel} accessibilityRole="button" accessibilityState={{ expanded }} onPress={onToggle} style={(focused, pressed, hovered) => [styles.selectButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}>
        <Text numberOfLines={1} style={styles.selectText}>{currentLabel}</Text>
        <Text style={[styles.selectChevron, expanded && styles.selectChevronOpen]}>⌄</Text>
      </FocusablePressable>
      {expanded ? <View accessibilityRole="menu" style={styles.selectMenu}>
        {options.map(([option, label]) => {
          const selected = option === value;
          return <FocusablePressable key={option} accessibilityLabel={label} accessibilityRole="menuitem" accessibilityState={{ selected }} onPress={() => onChange(option)} style={(focused, pressed, hovered) => [styles.selectOption, selected && styles.selectOptionSelected, (focused || hovered) && styles.selectOptionActive, pressed && styles.pressed]}>
            <Text style={[styles.selectOptionText, selected && styles.selectOptionTextSelected]}>{label}</Text>
            {selected ? <Text style={styles.selectCheck}>✓</Text> : null}
          </FocusablePressable>;
        })}
      </View> : null}
    </View>
  );
}

function SettingRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  const dropdownOpen = React.Children.toArray(children).some((child) => React.isValidElement(child) && Boolean((child.props as { expanded?: boolean }).expanded));
  return (
    <View style={[styles.settingRow, dropdownOpen && styles.settingRowExpanded]}>
      <View style={styles.settingCopy}>
        <Text style={styles.settingLabel}>{label}</Text>
        {description ? <Text style={styles.settingDescription}>{description}</Text> : null}
      </View>
      {children}
    </View>
  );
}

export function SettingsScreen({ navigation }: Props) {
  const { width } = useReferenceWindowDimensions();
  const bottomNavInset = useBottomNavInset();
  const compact = width <= 760;
  const modalWidth = Math.min(520, Math.max(280, width - 36));
  const { credentials, user, signOut } = useSession();
  const activeServerUrl = credentials?.serverUrl ?? REMUX_SERVER_URL;
  const viewsQuery = useQuery({ queryKey: ["settings", "views", credentials?.serverUrl, credentials?.userId], queryFn: () => getUserViews(credentials!), enabled: Boolean(credentials) });
  const orderedViews = useMemo(
    () => orderHomeUserViews(viewsQuery.data ?? [], user?.Configuration?.OrderedViews),
    [user?.Configuration?.OrderedViews, viewsQuery.data],
  );
  const userConfigQuery = useQuery({ queryKey: ["settings", "user-configuration", credentials?.serverUrl, credentials?.userId], queryFn: () => getUserConfiguration(credentials!), enabled: Boolean(credentials) });
  const displayPreferencesQuery = useQuery({ queryKey: ["settings", "playback-display-preferences", credentials?.serverUrl, credentials?.userId], queryFn: () => getPlaybackDisplayPreferences(credentials!), enabled: Boolean(credentials) });
  const userConfigHydrated = useRef(false);
  const displayPreferencesHydrated = useRef(false);
  const preferenceSaveQueue = useRef<Promise<void>>(Promise.resolve());
  const [homeConfig, setHomeConfig] = useState<NativeHomeConfig | null>(null);
  const [preferences, setPreferences] = useState<NativePreferences | null>(null);
  const [nextSection, setNextSection] = useState(0);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [editingType, setEditingType] = useState<string>(SECTION_OPTIONS[0][0]);
  const [addTypeExpanded, setAddTypeExpanded] = useState(false);
  const [editTypeExpanded, setEditTypeExpanded] = useState(false);
  const [routePending, setRoutePending] = useState(false);
  const [routeResults, setRouteResults] = useState<RouteResult[]>([]);
  const [routeError, setRouteError] = useState("");
  const [themeUploadVisible, setThemeUploadVisible] = useState(false);
  const [themeJson, setThemeJson] = useState("");
  const [themeError, setThemeError] = useState("");
  const [preferenceError, setPreferenceError] = useState("");
  const [expandedPlaybackChoice, setExpandedPlaybackChoice] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<SettingsCategory>("Account");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordPending, setPasswordPending] = useState(false);
  const [passwordStatus, setPasswordStatus] = useState("");
  const [discordLinkPending, setDiscordLinkPending] = useState(false);
  const [discordLinkCode, setDiscordLinkCode] = useState("");
  const [discordLinkUrl, setDiscordLinkUrl] = useState("");
  const discordIdentityQuery = useQuery({
    queryKey: ["settings", "discord-identities", credentials?.userId],
    queryFn: () => getDiscordIdentities(credentials!),
    enabled: Boolean(credentials && productProfile.kind !== "public"),
    refetchInterval: discordLinkPending ? 2000 : false,
  });
  const discordIdentity = discordIdentityQuery.data?.[0];
  useEffect(() => {
    if (discordLinkPending && discordIdentity) setDiscordLinkPending(false);
  }, [discordIdentity, discordLinkPending]);
  const discordLink = useMutation({
    mutationFn: () => initiateDiscordLink(credentials!),
    onSuccess: (result) => {
      const url = `${credentials!.serverUrl}${result.authorizeUrl}`;
      setDiscordLinkCode(result.code);
      setDiscordLinkUrl(Platform.isTV ? `${credentials!.serverUrl}/remux/auth/discord/authorize` : url);
      setDiscordLinkPending(true);
      if (Platform.OS === "web" && typeof window !== "undefined") window.open(url, "jellyflix-discord-link", "popup,width=520,height=760");
      else if (!Platform.isTV) void Linking.openURL(url);
    },
  });
  const discordUnlink = useMutation({
    mutationFn: () => unlinkDiscord(credentials!),
    onSuccess: () => { setDiscordLinkPending(false); setDiscordLinkCode(""); setDiscordLinkUrl(""); void discordIdentityQuery.refetch(); },
  });

  useEffect(() => {
    void Promise.all([readHomeConfig(), readPreferences()]).then(([home, playback]) => {
      setHomeConfig(home);
      setPreferences(playback);
    });
  }, []);
  useEffect(() => {
    if (productProfile.kind !== "public" || !credentials || !orderedViews.length) return;
    const defaultDisabledIds = user?.Configuration?.LatestItemsExcludes ?? [];
    const sourceKey = buildHomeUserViewsSourceKey(credentials.serverUrl, credentials.userId, orderedViews, defaultDisabledIds);
    setHomeConfig((current) => {
      if (!current) return current;
      const next = mergeHomeConfigUserViews(current, orderedViews, sourceKey, defaultDisabledIds);
      if (next === current) return current;
      void saveHomeConfig(next);
      return next;
    });
  }, [credentials, orderedViews, user?.Configuration?.LatestItemsExcludes]);
  useEffect(() => {
    if (!userConfigQuery.data || userConfigHydrated.current) return;
    userConfigHydrated.current = true;
    setPreferences((current) => {
      if (!current) return current;
      const next = {
        ...current,
        audioLanguage: userConfigQuery.data.AudioLanguagePreference ?? "",
        subtitleLanguage: userConfigQuery.data.SubtitleLanguagePreference ?? "",
        subtitleMode: userConfigQuery.data.SubtitleMode ?? current.subtitleMode,
        playDefaultAudio: userConfigQuery.data.PlayDefaultAudioTrack ?? current.playDefaultAudio,
        rememberAudioSelections: userConfigQuery.data.RememberAudioSelections ?? current.rememberAudioSelections,
        rememberSubtitleSelections: userConfigQuery.data.RememberSubtitleSelections ?? current.rememberSubtitleSelections,
        autoplayNext: userConfigQuery.data.EnableNextEpisodeAutoPlay ?? current.autoplayNext,
      };
      void savePreferences(next);
      return next;
    });
  }, [userConfigQuery.data]);
  useEffect(() => {
    if (!displayPreferencesQuery.data || displayPreferencesHydrated.current) return;
    displayPreferencesHydrated.current = true;
    const custom = displayPreferencesQuery.data.CustomPrefs;
    const parseBoolean = (key: string, fallback: boolean) => custom[key] === undefined ? fallback : custom[key] === "true";
    const parseSeconds = (key: string, fallback: number) => {
      const milliseconds = Number.parseInt(custom[key] ?? "", 10);
      return Number.isFinite(milliseconds) && milliseconds > 0 ? milliseconds / 1000 : fallback;
    };
    setPreferences((current) => {
      if (!current) return current;
      const next = {
        ...current,
        seekBackSeconds: parseSeconds("skipBackLength", current.seekBackSeconds),
        seekForwardSeconds: parseSeconds("skipForwardLength", current.seekForwardSeconds),
        showNextVideoInfoOverlay: parseBoolean("enableNextVideoInfoOverlay", current.showNextVideoInfoOverlay),
        showVideoRemainingTime: parseBoolean("enableVideoRemainingTime", current.showVideoRemainingTime),
      };
      void savePreferences(next);
      return next;
    });
  }, [displayPreferencesQuery.data]);

  const updateHome = (updater: (current: NativeHomeConfig) => NativeHomeConfig) => {
    setHomeConfig((current) => {
      if (!current) return current;
      const next = updater(current);
      void saveHomeConfig(next);
      return next;
    });
  };
  const updatePreference = <K extends keyof NativePreferences>(key: K, value: NativePreferences[K]) => {
    setPreferences((current) => {
      if (!current) return current;
      const next = { ...current, [key]: value };
      void savePreferences(next);
      return next;
    });
    if (!credentials) return;
    let patch: Partial<JellyfinUserConfiguration> | undefined;
    if (key === "audioLanguage") patch = { AudioLanguagePreference: String(value) || null };
    else if (key === "subtitleLanguage") patch = { SubtitleLanguagePreference: String(value) || null };
    else if (key === "subtitleMode") patch = { SubtitleMode: value as JellyfinUserConfiguration["SubtitleMode"] };
    else if (key === "playDefaultAudio") patch = { PlayDefaultAudioTrack: Boolean(value) };
    else if (key === "rememberAudioSelections") patch = { RememberAudioSelections: Boolean(value) };
    else if (key === "rememberSubtitleSelections") patch = { RememberSubtitleSelections: Boolean(value) };
    else if (key === "autoplayNext") patch = { EnableNextEpisodeAutoPlay: Boolean(value) };
    let displayPatch: Record<string, string> | undefined;
    if (key === "seekBackSeconds") displayPatch = { skipBackLength: String(Number(value) * 1000) };
    else if (key === "seekForwardSeconds") displayPatch = { skipForwardLength: String(Number(value) * 1000) };
    else if (key === "showNextVideoInfoOverlay") displayPatch = { enableNextVideoInfoOverlay: String(Boolean(value)) };
    else if (key === "showVideoRemainingTime") displayPatch = { enableVideoRemainingTime: String(Boolean(value)) };
    if (patch) {
      const serverPatch = patch;
      setPreferenceError("");
      preferenceSaveQueue.current = preferenceSaveQueue.current
        .catch(() => undefined)
        .then(() => updateUserConfiguration(credentials, serverPatch))
        .catch(() => setPreferenceError("Failed to save playback preferences to the server."));
    }
    if (displayPatch) {
      const serverDisplayPatch = displayPatch;
      setPreferenceError("");
      preferenceSaveQueue.current = preferenceSaveQueue.current
        .catch(() => undefined)
        .then(() => updatePlaybackDisplayPreferences(credentials, serverDisplayPatch))
        .catch(() => setPreferenceError("Failed to save playback preferences to the server."));
    }
  };
  const updateMediaSegmentAction = (type: MediaSegmentType, action: MediaSegmentAction) => {
    setPreferences((current) => {
      if (!current) return current;
      const next = { ...current, mediaSegmentActions: { ...current.mediaSegmentActions, [type]: action } };
      void savePreferences(next);
      return next;
    });
    setExpandedPlaybackChoice(null);
  };
  const activeCount = useMemo(() => homeConfig?.sections.filter((section) => section.enabled).length ?? 0, [homeConfig]);
  const runRouteTest = async () => {
    if (routePending) return;
    setRoutePending(true);
    setRouteError("");
    const measured = await Promise.all(ROUTE_CANDIDATES.map(async (candidate) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      const started = Date.now();
      try {
        const response = await fetch(`${candidate.url}/System/Ping?jfx_speedtest=${started}`, { signal: controller.signal });
        return { label: candidate.label, host: hostName(candidate.url), latencyMs: response.status < 500 ? Date.now() - started : null };
      } catch {
        return { label: candidate.label, host: hostName(candidate.url), latencyMs: null };
      } finally {
        clearTimeout(timeout);
      }
    }));
    setRouteResults(measured);
    if (measured.every((result) => result.latencyMs === null)) setRouteError("Route test completed, but no healthy route was detected.");
    setRoutePending(false);
  };
  const submitPassword = async () => {
    if (!credentials || passwordPending) return;
    if (newPassword.length < 8) {
      setPasswordStatus("Use at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordStatus("New passwords do not match.");
      return;
    }
    setPasswordPending(true);
    setPasswordStatus("");
    try {
      await changeOwnPassword(credentials, credentials.userId, currentPassword, newPassword);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setPasswordStatus("Password updated. You are still signed in.");
    } catch (error) {
      setPasswordStatus(error instanceof Error ? error.message : "Password update failed.");
    } finally {
      setPasswordPending(false);
    }
  };

  if (!homeConfig || !preferences) {
    return <SafeAreaView style={styles.safe}><ActivityIndicator color={colors.brand} style={styles.loader} /><MatureTopNav navigation={navigation} active="Settings" /></SafeAreaView>;
  }

  const moveSection = (id: string, direction: -1 | 1) => updateHome((current) => {
    const sections = [...current.sections];
    const index = sections.findIndex((section) => section.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= sections.length) return current;
    const [section] = sections.splice(index, 1);
    sections.splice(target, 0, section);
    return { ...current, sections };
  });
  const renderSection = ({ item, index }: { item: NativeHomeSection; index: number }) => (
    <View style={styles.sectionCard}>
      <View style={styles.sectionTopline}>
        <View style={styles.arrowColumn}>
          <ActionButton label={`Move ${item.title} up`} onPress={() => moveSection(item.id, -1)}><ArrowUp color={index === 0 ? "#555" : "#aaa"} size={13} /></ActionButton>
          <ActionButton label={`Move ${item.title} down`} onPress={() => moveSection(item.id, 1)}><ArrowDown color={index === homeConfig.sections.length - 1 ? "#555" : "#aaa"} size={13} /></ActionButton>
        </View>
        <View style={styles.sectionCopy}>
          <Text numberOfLines={1} style={styles.sectionTitle}>{item.title}</Text>
          <Text style={styles.sectionType}>{item.type}</Text>
        </View>
        <FocusablePressable accessibilityLabel={`Edit ${item.title}`} onPress={() => { setEditingSectionId(item.id); setEditingTitle(item.title); setEditingType(item.type); setEditTypeExpanded(false); }} style={(focused, pressed, hovered) => [styles.textAction, (focused || hovered) && styles.iconButtonActive, pressed && styles.pressed]}><Text style={styles.textActionLabel}>Edit</Text></FocusablePressable>
        <Toggle value={item.enabled} onChange={(enabled) => updateHome((current) => ({ ...current, sections: current.sections.map((section) => section.id === item.id ? { ...section, enabled } : section) }))} />
        <FocusablePressable accessibilityLabel={`Remove ${item.title}`} onPress={() => updateHome((current) => ({ ...current, sections: current.sections.filter((section) => section.id !== item.id) }))} style={(focused, pressed, hovered) => [styles.textAction, styles.textActionDanger, (focused || hovered) && styles.removeActive, pressed && styles.pressed]}><Text style={styles.removeLabel}>Remove</Text></FocusablePressable>
      </View>
    </View>
  );

  const addSectionControls = (
    <View style={[styles.addRow, compact && styles.addRowCompact]}>
      <SectionTypeSelect
        accessibilityLabel="Choose section type to add"
        value={SECTION_OPTIONS[nextSection][0]}
        expanded={addTypeExpanded}
        onToggle={() => setAddTypeExpanded((value) => !value)}
        onChange={(type) => {
          const index = SECTION_OPTIONS.findIndex(([optionType]) => optionType === type);
          if (index >= 0) setNextSection(index);
          setAddTypeExpanded(false);
        }}
      />
      <FocusablePressable onPress={() => { const [type, title] = SECTION_OPTIONS[nextSection]; updateHome((current) => ({ ...current, sections: [...current.sections, { id: `native-${Date.now()}`, type, title, enabled: true }] })); }} style={(focused, pressed, hovered) => [styles.primaryButton, (focused || hovered) && styles.primaryButtonActive, pressed && styles.pressed]}>
        <Plus color="#fff" size={16} /><Text style={styles.primaryButtonText}>Add Section</Text>
      </FocusablePressable>
    </View>
  );
  const playerPanel = (
    <Panel eyebrow="Player" title="Controls" description="Choose how the player behaves and which controls are available.">
      <SettingRow label="All controls" description={`${preferences.playerControls ? PLAYER_CONTROL_GROUPS.reduce((total, group) => total + group.controls.length, 0) - Object.values(preferences.playerControlVisibility).filter((enabled) => enabled === false).length : 0} enabled`}><Toggle value={preferences.playerControls} onChange={(value) => updatePreference("playerControls", value)} /></SettingRow>
      <FlatList
        data={PLAYER_CONTROL_GROUPS}
        keyExtractor={(group) => group.title}
        scrollEnabled={false}
        renderItem={({ item: group }) => <View style={styles.controlGroup}><Text style={styles.sectionLabel}>{group.title.toUpperCase()}</Text><FlatList data={group.controls} keyExtractor={(control) => control[0]} scrollEnabled={false} renderItem={({ item: control }) => <SettingRow label={control[1]}><Toggle value={preferences.playerControls && preferences.playerControlVisibility[control[0]] !== false} onChange={(enabled) => updatePreference("playerControlVisibility", { ...preferences.playerControlVisibility, [control[0]]: enabled })} /></SettingRow>} /></View>}
      />
      <FocusablePressable onPress={() => updatePreference("playerControlVisibility", {})} style={(focused, pressed, hovered) => [styles.neutralButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>Reset Player Controls</Text></FocusablePressable>
    </Panel>
  );
  const supportPanels = (
    <>
      {activeCategory === "Account" ? <Panel eyebrow="Account" title={user?.Name ?? "Jellyflix user"} description="Signed in and ready to watch.">
        <View style={[styles.miniGrid, compact && styles.miniGridCompact]}><StatusPill compact={compact} label="Color" value="dark" /><StatusPill compact={compact} label="Route" value={hostName(activeServerUrl)} /></View>
        {productProfile.kind !== "public" ? <View style={styles.discordAccount}>
          <Text style={styles.sectionLabel}>DISCORD</Text>
          <Text style={styles.mutedCopy}>{discordIdentity ? `Linked as ${discordIdentity.provider_username}` : "Link Discord for guild sign-in and the Jellyflix role."}</Text>
          {discordLinkPending ? <><Text selectable style={styles.discordLinkCopy}>Open {discordLinkUrl}</Text><Text style={styles.discordCode}>Code: {discordLinkCode}</Text></> : null}
          <FocusablePressable disabled={discordLink.isPending || discordUnlink.isPending} onPress={() => discordIdentity ? discordUnlink.mutate() : discordLink.mutate()} style={(focused, pressed, hovered) => [styles.discordAction, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.discordActionText}>{discordIdentity ? "Unlink Discord" : discordLinkPending ? "Waiting for Discord..." : "Link Discord"}</Text></FocusablePressable>
          {discordLink.error || discordUnlink.error || discordIdentityQuery.error ? <Text style={styles.errorCopy}>{(discordLink.error || discordUnlink.error || discordIdentityQuery.error)?.message}</Text> : null}
        </View> : null}
        <FocusablePressable onPress={() => { setPasswordStatus(""); setPasswordVisible(true); }} style={(focused, pressed, hovered) => [styles.neutralButton, styles.accountAction, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><KeyRound color="#fff" size={16} /><Text style={styles.neutralButtonText}>Change Password</Text></FocusablePressable>
        <FocusablePressable onPress={() => void signOut()} style={(focused, pressed, hovered) => [styles.signOut, (focused || hovered) && styles.signOutActive, pressed && styles.pressed]}><LogOut color="#fff" size={16} /><Text style={styles.signOutText}>Sign Out</Text></FocusablePressable>
      </Panel> : null}
      {activeCategory === "Connection & Advanced" ? <Panel eyebrow="Server Route" title={productProfile.aggressiveOptimizationsEnabled ? "Route Speed" : "Connected Server"} description={productProfile.aggressiveOptimizationsEnabled ? "Compare the available routes without interrupting playback." : "This build talks directly to the Remux server selected at sign in."}>
        <Text style={styles.routeActive}>Active route: <Text style={styles.routeStrong}>{hostName(activeServerUrl)}</Text></Text>
        {productProfile.aggressiveOptimizationsEnabled ? <>
          <FocusablePressable accessibilityLabel="Run Route Speed Test" onPress={() => void runRouteTest()} style={(focused, pressed, hovered) => [styles.neutralButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>{routePending ? "Testing routes..." : "Run Route Speed Test"}</Text></FocusablePressable>
          {routeError ? <Text style={styles.errorCopy}>{routeError}</Text> : null}
          {routeResults.length ? <FlatList data={routeResults} keyExtractor={(result) => result.host} scrollEnabled={false} ItemSeparatorComponent={() => <View style={styles.itemGap} />} renderItem={({ item }) => <View style={styles.routeResult}><View><Text style={styles.routeResultTitle}>{item.label}</Text><Text style={styles.mutedCopy}>{item.host}</Text></View><Text style={styles.routeLatency}>{item.latencyMs === null ? "Unavailable" : `${item.latencyMs} ms`}</Text></View>} /> : <Text style={styles.mutedCopy}>No benchmark yet. Run the route speed test to compare both Jellyfin URLs.</Text>}
        </> : null}
      </Panel> : null}
      {activeCategory === "Home" ? <Panel eyebrow="Quick Controls" title="Home Defaults" description="Control the showcase and recommendation shelf count.">
        <SettingRow label="Hero Showcase"><Toggle value={homeConfig.showcase} onChange={(value) => updateHome((current) => ({ ...current, showcase: value }))} /></SettingRow>
        <SettingRow label="Extra random rows"><FocusablePressable onPress={() => updateHome((current) => ({ ...current, dynamicSectionLimit: (current.dynamicSectionLimit + 1) % 21 }))} style={(focused, pressed, hovered) => [styles.numberButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.numberText}>{homeConfig.dynamicSectionLimit}</Text></FocusablePressable></SettingRow>
        <FocusablePressable onPress={() => void resetHomeConfig().then(setHomeConfig)} style={(focused, pressed, hovered) => [styles.neutralButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>Reset to Default</Text></FocusablePressable>
      </Panel> : null}
      {activeCategory === "Playback" ? <Panel eyebrow="Playback" title="Playback Behavior" description="Preferred tracks, subtitles, autoplay, and recovery.">
        {preferenceError ? <Text style={styles.errorCopy}>{preferenceError}</Text> : null}
        <SettingRow label="Playback quality" description={preferences.playbackQuality === "original" ? "Preserve the selected source quality during recovery." : "Allow a compatible fallback after a confirmed playback failure."}><ChoiceSelect value={preferences.playbackQuality} options={[["original", "Original fidelity"], ["adaptive", "Adaptive"]] as const} expanded={expandedPlaybackChoice === "playback-quality"} onToggle={() => setExpandedPlaybackChoice((current) => current === "playback-quality" ? null : "playback-quality")} onChange={(value) => { updatePreference("playbackQuality", value); setExpandedPlaybackChoice(null); }} accessibilityLabel="Playback quality" /></SettingRow>
        <SettingRow label="Simple recovery"><Toggle value={preferences.simplePlaybackRecovery} onChange={(value) => updatePreference("simplePlaybackRecovery", value)} /></SettingRow>
        <SettingRow label="Play default audio"><Toggle value={preferences.playDefaultAudio} onChange={(value) => updatePreference("playDefaultAudio", value)} /></SettingRow>
        <SettingRow label="Keep preferred tracks selected" description="Jellyflix restores preferred audio and subtitles when matches exist."><Toggle value={preferences.keepPreferredTracks} onChange={(value) => updatePreference("keepPreferredTracks", value)} /></SettingRow>
        <SettingRow label="Remember audio"><Toggle value={preferences.rememberAudioSelections} onChange={(value) => updatePreference("rememberAudioSelections", value)} /></SettingRow>
        <SettingRow label="Remember subtitles"><Toggle value={preferences.rememberSubtitleSelections} onChange={(value) => updatePreference("rememberSubtitleSelections", value)} /></SettingRow>
        <SettingRow label="Autoplay next episode"><Toggle value={preferences.autoplayNext} onChange={(value) => updatePreference("autoplayNext", value)} /></SettingRow>
        <SettingRow label="Show next-video overlay" description="Shared with Jellyfin Web through your server profile."><Toggle value={preferences.showNextVideoInfoOverlay} onChange={(value) => updatePreference("showNextVideoInfoOverlay", value)} /></SettingRow>
        <SettingRow label="Show remaining time" description="Show time remaining instead of total duration in the player."><Toggle value={preferences.showVideoRemainingTime} onChange={(value) => updatePreference("showVideoRemainingTime", value)} /></SettingRow>
        <SettingRow label="Rewind length"><ChoiceSelect value={String(preferences.seekBackSeconds)} options={SEEK_LENGTH_OPTIONS} expanded={expandedPlaybackChoice === "seek-back"} onToggle={() => setExpandedPlaybackChoice((current) => current === "seek-back" ? null : "seek-back")} onChange={(value) => { updatePreference("seekBackSeconds", Number(value)); setExpandedPlaybackChoice(null); }} accessibilityLabel="Rewind length" /></SettingRow>
        <SettingRow label="Fast-forward length"><ChoiceSelect value={String(preferences.seekForwardSeconds)} options={SEEK_LENGTH_OPTIONS} expanded={expandedPlaybackChoice === "seek-forward"} onToggle={() => setExpandedPlaybackChoice((current) => current === "seek-forward" ? null : "seek-forward")} onChange={(value) => { updatePreference("seekForwardSeconds", Number(value)); setExpandedPlaybackChoice(null); }} accessibilityLabel="Fast-forward length" /></SettingRow>
        <SettingRow label="Default audio language"><FocusablePressable onPress={() => updatePreference("audioLanguage", preferences.audioLanguage === "eng" ? "" : "eng")} style={(focused, pressed, hovered) => [styles.compactChoice, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.choiceText}>{preferences.audioLanguage === "eng" ? "English" : "None"}</Text></FocusablePressable></SettingRow>
        <SettingRow label="Subtitle mode"><FocusablePressable onPress={() => { const modes: NativePreferences["subtitleMode"][] = ["Default", "Smart", "Always", "OnlyForced", "None"]; const index = modes.indexOf(preferences.subtitleMode); updatePreference("subtitleMode", modes[(index + 1) % modes.length]); }} style={(focused, pressed, hovered) => [styles.compactChoice, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.choiceText}>{preferences.subtitleMode === "OnlyForced" ? "Forced only" : preferences.subtitleMode}</Text></FocusablePressable></SettingRow>
        <Text style={styles.sectionLabel}>MEDIA SEGMENT ACTIONS</Text>
        {MEDIA_SEGMENT_TYPES.map(([type, label]) => <SettingRow key={type} label={label} description={type === "Intro" ? "Uses Jellyfin's Skip, AskToSkip, and None behavior." : undefined}><ChoiceSelect value={preferences.mediaSegmentActions[type]} options={MEDIA_SEGMENT_ACTION_OPTIONS} expanded={expandedPlaybackChoice === `segment-${type}`} onToggle={() => setExpandedPlaybackChoice((current) => current === `segment-${type}` ? null : `segment-${type}`)} onChange={(action) => updateMediaSegmentAction(type, action)} accessibilityLabel={`${label} action`} /></SettingRow>)}
      </Panel> : null}
      {activeCategory === "Appearance" ? <Panel eyebrow="Themes" title="Appearance" description="Choose interface density and motion behavior.">
        <SettingRow label="Navigation style" description="Auto uses the side rail on TV displays and the top bar elsewhere."><ChoiceSelect value={preferences.navigationMode} options={[["auto", "Auto"], ["topbar", "Top bar"], ["sidebar", "Side rail"]] as const} expanded={expandedPlaybackChoice === "navigation-style"} onToggle={() => setExpandedPlaybackChoice((current) => current === "navigation-style" ? null : "navigation-style")} onChange={(value) => { updatePreference("navigationMode", value); setExpandedPlaybackChoice(null); }} accessibilityLabel="Navigation style" /></SettingRow>
        <SettingRow label="Centered hero" description="Auto centers the featured title, description, and actions on TV displays."><ChoiceSelect value={preferences.centeredHero} options={[['auto', 'Auto'], ['on', 'On'], ['off', 'Off']] as const} expanded={expandedPlaybackChoice === "centered-hero"} onToggle={() => setExpandedPlaybackChoice((current) => current === "centered-hero" ? null : "centered-hero")} onChange={(value) => { updatePreference("centeredHero", value); setExpandedPlaybackChoice(null); }} accessibilityLabel="Centered hero" /></SettingRow>
        <SettingRow label="Color mode"><ChoiceSelect value={preferences.colorMode} options={[["dark", "Dark"], ["light", "Light"], ["system", "System"]] as const} expanded={expandedPlaybackChoice === "color-mode"} onToggle={() => setExpandedPlaybackChoice((current) => current === "color-mode" ? null : "color-mode")} onChange={(value) => { updatePreference("colorMode", value); setExpandedPlaybackChoice(null); }} accessibilityLabel="Color mode" /></SettingRow>
        <SettingRow label="Server theme"><ChoiceSelect value={preferences.serverThemeId || "default"} options={[["default", "Default server theme"]] as const} expanded={expandedPlaybackChoice === "server-theme"} onToggle={() => setExpandedPlaybackChoice((current) => current === "server-theme" ? null : "server-theme")} onChange={() => setExpandedPlaybackChoice(null)} accessibilityLabel="Server theme" /></SettingRow>
        <SettingRow label="Vertical lists on mobile" description="Episodes and similar titles stack as scrollable rows in portrait."><Toggle value={preferences.mobileEpisodeList} onChange={(value) => updatePreference("mobileEpisodeList", value)} /></SettingRow>
        <SettingRow label="Titles on search posters" description="Embed each title along the bottom of its search result poster."><Toggle value={preferences.searchCardTitles} onChange={(value) => updatePreference("searchCardTitles", value)} /></SettingRow>
        <SettingRow label="Bottom navbar on mobile"><Toggle value={preferences.bottomNavMobile} onChange={(value) => updatePreference("bottomNavMobile", value)} /></SettingRow>
        {Platform.OS === "web" ? <SettingRow label="TV presentation on Web" description="Use television viewing-distance sizing and focus treatment in this browser. A tv=1 link remains a temporary override."><Toggle value={preferences.webTelevisionMode} onChange={(value) => { setWebTelevisionMode(value); updatePreference("webTelevisionMode", value); }} /></SettingRow> : null}
        <SettingRow label="TV navigation sounds" description="Play Jellyflix movement and action cues, including player menus."><Toggle value={preferences.uiSounds} onChange={(value) => updatePreference("uiSounds", value)} /></SettingRow>
        <SettingRow label="Controller haptics" description="Use controller rumble on supported Web and TV devices."><Toggle value={preferences.controllerHaptics} onChange={(value) => updatePreference("controllerHaptics", value)} /></SettingRow>
        <SettingRow label="Compact media rows"><Toggle value={preferences.compactRows} onChange={(value) => updatePreference("compactRows", value)} /></SettingRow>
        <SettingRow label="Reduce motion"><Toggle value={preferences.reduceMotion} onChange={(value) => updatePreference("reduceMotion", value)} /></SettingRow>
        {productProfile.privateBackendEnabled && user?.Policy?.IsAdministrator ? <FocusablePressable onPress={() => { setThemeError(""); setThemeUploadVisible(true); }} style={(focused, pressed, hovered) => [styles.neutralButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>Upload theme</Text></FocusablePressable> : null}
      </Panel> : null}
      {activeCategory === "Libraries" ? <Panel eyebrow="Libraries" title="Recently Added" description="Choose which libraries feed Recently Added rows.">
        <FlatList
          data={(orderedViews.length ? orderedViews.map((view) => ({ libraryId: view.Id ?? view.Name ?? "library", libraryName: view.Name ?? "Library", collectionType: view.CollectionType?.toLocaleLowerCase(), enabled: homeConfig.libraryToggles.find((entry) => entry.libraryId === view.Id || entry.libraryName === view.Name)?.enabled ?? true })) : homeConfig.libraryToggles)}
          keyExtractor={(item) => item.libraryId}
          scrollEnabled={false}
          renderItem={({ item }) => <SettingRow label={item.libraryName}><Toggle value={item.enabled} onChange={(enabled) => updateHome((current) => ({ ...current, libraryToggles: [...current.libraryToggles.filter((entry) => entry.libraryId !== item.libraryId && entry.libraryName !== item.libraryName), { ...item, enabled }] }))} /></SettingRow>}
        />
      </Panel> : null}
    </>
  );
  const categoryRail = (
    <View style={[styles.categoryRail, compact && styles.categoryRailCompact]}>
      {SETTINGS_CATEGORIES.map((category) => {
        const selected = category === activeCategory;
        return (
          <FocusablePressable
            key={category}
            accessibilityState={{ selected }}
            onPress={() => setActiveCategory(category)}
            style={(focused, pressed, hovered) => [
              styles.categoryButton,
              compact && styles.categoryButtonCompact,
              selected && styles.categoryButtonSelected,
              (focused || hovered) && styles.categoryButtonActive,
              pressed && styles.pressed,
            ]}
          >
            <Text style={[styles.categoryLabel, selected && styles.categoryLabelSelected]}>{category}</Text>
            {!compact ? <ArrowRight color={selected ? "#fff" : "#766e71"} size={16} /> : null}
          </FocusablePressable>
        );
      })}
    </View>
  );
  const homeSectionsPanel = activeCategory === "Home" ? (
    <Panel eyebrow="Home" title="Home Sections" description="Choose what appears on the landing page and set the shelf order.">
      <Text style={styles.sectionLabel}>SECTIONS</Text>
      <FlatList data={homeConfig.sections} keyExtractor={(item) => item.id} scrollEnabled={false} renderItem={renderSection} ItemSeparatorComponent={() => <View style={styles.itemGap} />} />
      {addSectionControls}
    </Panel>
  ) : null;
  const editorModal = (
    <Modal visible={Boolean(editingSectionId)} transparent animationType="fade" onRequestClose={() => { setEditTypeExpanded(false); setEditingSectionId(null); }}>
      <View testID="tv-focus-dialog" style={styles.modalScrim}><View style={[styles.modalCard, { width: modalWidth }]}>
        <Text style={styles.panelEyebrow}>HOME SECTION</Text><Text style={styles.modalTitle}>Edit Section</Text><Text style={styles.fieldLabel}>TITLE</Text>
        <TextInput value={editingTitle} onChangeText={setEditingTitle} placeholder="Section title" placeholderTextColor="#6f686b" selectionColor={colors.brand} style={styles.textInput} />
        <Text style={styles.fieldLabel}>SECTION TYPE</Text>
        <SectionTypeSelect
          accessibilityLabel="Change section type"
          value={editingType}
          expanded={editTypeExpanded}
          onToggle={() => setEditTypeExpanded((value) => !value)}
          onChange={(type) => { setEditingType(type); setEditTypeExpanded(false); }}
        />
        <View style={styles.modalActions}>
          <FocusablePressable onPress={() => { setEditTypeExpanded(false); setEditingSectionId(null); }} style={(focused, pressed, hovered) => [styles.neutralButton, styles.modalButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>Cancel</Text></FocusablePressable>
          <FocusablePressable onPress={() => { const title = editingTitle.trim(); if (editingSectionId && title) updateHome((current) => ({ ...current, sections: current.sections.map((section) => section.id === editingSectionId ? { ...section, title, type: editingType } : section) })); setEditTypeExpanded(false); setEditingSectionId(null); }} style={(focused, pressed, hovered) => [styles.primaryButton, styles.modalButton, (focused || hovered) && styles.primaryButtonActive, pressed && styles.pressed]}><Text style={styles.primaryButtonText}>Save Section</Text></FocusablePressable>
        </View>
      </View></View>
    </Modal>
  );
  const themeModal = (
    <Modal visible={themeUploadVisible} transparent animationType="fade" onRequestClose={() => setThemeUploadVisible(false)}>
      <View testID="tv-focus-dialog" style={styles.modalScrim}><View style={[styles.modalCard, { width: modalWidth }]}>
        <Text style={styles.panelEyebrow}>THEMES</Text><Text style={styles.modalTitle}>Upload Theme</Text><Text style={styles.panelDescription}>Paste the Jellyflix theme JSON below.</Text>
        <TextInput multiline value={themeJson} onChangeText={setThemeJson} placeholder="{ ... }" placeholderTextColor="#6f686b" selectionColor={colors.brand} style={[styles.textInput, styles.jsonInput]} />
        {themeError ? <Text style={styles.errorCopy}>{themeError}</Text> : null}
        <View style={styles.modalActions}><FocusablePressable onPress={() => setThemeUploadVisible(false)} style={(focused, pressed, hovered) => [styles.neutralButton, styles.modalButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>Cancel</Text></FocusablePressable><FocusablePressable onPress={() => { void (async () => { try { const parsed = JSON.parse(themeJson); const response = await fetch(`https://jellyflix.app/api/jellyflix/themes?jellyfin_url=${encodeURIComponent(REMUX_SERVER_URL)}`, { method: "POST", headers: { "Content-Type": "application/json", Authorization: credentials?.accessToken ?? "" }, body: JSON.stringify(parsed) }); if (!response.ok) throw new Error("Theme upload failed."); setThemeUploadVisible(false); setThemeJson(""); } catch (error) { setThemeError(error instanceof SyntaxError ? "Theme upload file is not valid JSON." : "Failed to upload theme."); } })(); }} style={(focused, pressed, hovered) => [styles.primaryButton, styles.modalButton, (focused || hovered) && styles.primaryButtonActive, pressed && styles.pressed]}><Text style={styles.primaryButtonText}>Upload</Text></FocusablePressable></View>
      </View></View>
    </Modal>
  );
  const passwordModal = (
    <Modal visible={passwordVisible} transparent animationType="fade" onRequestClose={() => setPasswordVisible(false)}>
      <View testID="tv-focus-dialog" style={styles.modalScrim}><View style={[styles.modalCard, { width: modalWidth }]}>
        <Text style={styles.panelEyebrow}>ACCOUNT</Text>
        <Text style={styles.modalTitle}>Change Password</Text>
        <Text style={styles.panelDescription}>Update your password without signing out this device.</Text>
        <Text style={styles.fieldLabel}>CURRENT PASSWORD</Text>
        <TextInput secureTextEntry value={currentPassword} onChangeText={setCurrentPassword} autoComplete="current-password" selectionColor={colors.brand} style={styles.textInput} />
        <Text style={styles.fieldLabel}>NEW PASSWORD</Text>
        <TextInput secureTextEntry value={newPassword} onChangeText={setNewPassword} autoComplete="new-password" selectionColor={colors.brand} style={styles.textInput} />
        <Text style={styles.fieldLabel}>CONFIRM NEW PASSWORD</Text>
        <TextInput secureTextEntry value={confirmPassword} onChangeText={setConfirmPassword} autoComplete="new-password" selectionColor={colors.brand} style={styles.textInput} />
        {passwordStatus ? <Text style={passwordStatus.startsWith("Password updated") ? styles.successCopy : styles.errorCopy}>{passwordStatus}</Text> : null}
        <View style={styles.modalActions}>
          <FocusablePressable onPress={() => setPasswordVisible(false)} style={(focused, pressed, hovered) => [styles.neutralButton, styles.modalButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>Close</Text></FocusablePressable>
          <FocusablePressable disabled={passwordPending} onPress={() => void submitPassword()} style={(focused, pressed, hovered) => [styles.primaryButton, styles.modalButton, (focused || hovered) && styles.primaryButtonActive, pressed && styles.pressed]}><Text style={styles.primaryButtonText}>{passwordPending ? "Updating..." : "Update Password"}</Text></FocusablePressable>
        </View>
      </View></View>
    </Modal>
  );

  return (
    <SafeAreaView style={styles.safe} edges={[]}>
      <ScrollView contentContainerStyle={[styles.content, compact && styles.contentCompact, bottomNavInset ? { paddingBottom: bottomNavInset } : undefined]}>
        <View style={[styles.topline, compact && styles.toplineCompact]}>
          <View style={styles.titleBlock}>
            <Text style={styles.kicker}>JELLYFLIX</Text>
            <Text style={[styles.title, compact && styles.titleCompact]}>Settings</Text>
            <Text style={styles.subtitle}>Account, playback, home, and device preferences.</Text>
          </View>
          {!compact ? <View style={styles.statusStrip}>
            <StatusPill label="Sections" value={`${activeCount} active`} tone="brand" />
            <StatusPill label="Recovery" value={preferences.simplePlaybackRecovery ? "Simple" : "Off"} tone={preferences.simplePlaybackRecovery ? "success" : undefined} />
            <StatusPill label="Route" value={hostName(activeServerUrl)} />
          </View> : null}
        </View>
        {compact ? <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.categoryScroller}>{categoryRail}</ScrollView> : null}
        <View style={[styles.settingsWorkspace, compact && styles.settingsWorkspaceCompact]}>
          {!compact ? categoryRail : null}
          <View style={styles.categoryContent}>
            <View style={styles.categoryHeading}>
              <Text style={styles.panelEyebrow}>SETTINGS</Text>
              <Text style={styles.categoryTitle}>{activeCategory}</Text>
            </View>
            {homeSectionsPanel}
            {activeCategory === "Player Controls" ? playerPanel : null}
            {supportPanels}
            {activeCategory === "Player Controls" ? <FocusablePressable onPress={() => { setPreferences(defaultPreferences); void savePreferences(defaultPreferences); }} style={(focused, pressed, hovered) => [styles.neutralButton, styles.resetButton, (focused || hovered) && styles.controlActive, pressed && styles.pressed]}><Text style={styles.neutralButtonText}>Reset Player Controls</Text></FocusablePressable> : null}
          </View>
        </View>
      </ScrollView>
      {editorModal}{productProfile.privateBackendEnabled ? themeModal : null}{passwordModal}
      <MatureTopNav navigation={navigation} active="Settings" overlay />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: "#000" },
  loader: { marginTop: 180 },
  content: { paddingTop: 116, paddingHorizontal: 56, paddingBottom: 100 },
  contentCompact: { paddingTop: 160, paddingHorizontal: 14 },
  topline: { alignSelf: "stretch", flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end", gap: 28, marginBottom: 28 },
  toplineCompact: { alignItems: "stretch", flexDirection: "column", gap: 22, marginBottom: 22 },
  titleBlock: { flexShrink: 1 },
  kicker: { color: "#858085", fontSize: 12, lineHeight: 17, fontWeight: "800" },
  title: { marginTop: 5, color: "#fff", fontSize: 42, lineHeight: 48, fontWeight: "900", letterSpacing: -1.1 },
  titleCompact: { fontSize: 38, lineHeight: 44 },
  subtitle: { marginTop: 9, maxWidth: 580, color: "#aaa4aa", fontSize: 16, lineHeight: 25 },
  statusStrip: { flex: 1.1, maxWidth: 720, flexDirection: "row", flexWrap: "wrap", gap: 10 },
  statusStripCompact: { flexGrow: 0, flexShrink: 0, flexBasis: "auto", maxWidth: undefined, flexDirection: "column", flexWrap: "nowrap", gap: 10 },
  statusPill: { flexGrow: 1, flexBasis: 280, minHeight: 64, paddingHorizontal: 12, paddingVertical: 11, borderWidth: 1, borderColor: "#34282a", borderRadius: 8, justifyContent: "center", backgroundColor: "#130d0e" },
  statusPillCompact: { flexGrow: 0, flexBasis: "auto" },
  statusBrand: { borderColor: "#9e0710", backgroundColor: "#310002" },
  statusSuccess: { borderColor: "#135e2d", backgroundColor: "#071d0c" },
  statusLabel: { color: "#80797d", fontSize: 11, lineHeight: 15, fontWeight: "800" },
  statusValue: { marginTop: 5, color: "#f5f2f3", fontSize: 14, lineHeight: 19, fontWeight: "800" },
  dashboard: { alignSelf: "stretch", flexDirection: "row", alignItems: "flex-start", gap: 20 },
  dashboardCompact: { flexDirection: "column" },
  settingsWorkspace: { alignSelf: "stretch", flexDirection: "row", alignItems: "flex-start", gap: 24 },
  settingsWorkspaceCompact: { flexDirection: "column", gap: 16 },
  categoryScroller: { paddingBottom: 4 },
  categoryRail: { width: 250, padding: 8, gap: 5, borderWidth: 1, borderColor: "#34282a", borderRadius: 10, backgroundColor: "#130d0e" },
  categoryRailCompact: { width: "auto", flexDirection: "row", padding: 0, borderWidth: 0, backgroundColor: "transparent" },
  categoryButton: { minHeight: 46, paddingHorizontal: 13, borderRadius: 7, flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10 },
  categoryButtonCompact: { minHeight: 40, paddingHorizontal: 14, borderWidth: 1, borderColor: "#34282a", backgroundColor: "#130d0e" },
  categoryButtonSelected: { backgroundColor: "#4a0509" },
  categoryButtonActive: { backgroundColor: "#33272a" },
  categoryLabel: { color: "#aaa4aa", fontSize: 14, lineHeight: 19, fontWeight: "700" },
  categoryLabelSelected: { color: "#fff" },
  categoryContent: { flex: 1, minWidth: 0, gap: 20 },
  categoryHeading: { paddingBottom: 2 },
  categoryTitle: { marginTop: 4, color: "#fff", fontSize: 30, lineHeight: 36, fontWeight: "900", letterSpacing: -0.5 },
  primaryStack: { flex: 1.55, gap: 20 },
  supportStack: { flex: 1, gap: 20 },
  stackCompact: { flexGrow: 0, flexShrink: 0, flexBasis: "auto" },
  panel: { padding: 20, borderWidth: 1, borderColor: "#382326", borderRadius: 8, backgroundColor: "#1a090b" },
  panelEyebrow: { color: "#8d8588", fontSize: 11, lineHeight: 15, fontWeight: "900" },
  panelTitle: { marginTop: 5, color: "#f5f2f3", fontSize: 21, lineHeight: 27, fontWeight: "900" },
  panelDescription: { marginTop: 5, color: "#aaa4aa", fontSize: 14, lineHeight: 21 },
  panelBody: { position: "relative", zIndex: 1, overflow: "visible", marginTop: 20, gap: 12 },
  discordAccount: { marginTop: 18, padding: 14, gap: 9, borderWidth: 1, borderColor: "#343b78", borderRadius: 8, backgroundColor: "#111536" },
  discordAction: { minHeight: 44, marginTop: 3, borderRadius: 7, alignItems: "center", justifyContent: "center", backgroundColor: "#5865F2" },
  discordActionText: { color: "#fff", fontSize: 14, fontWeight: "900" },
  discordLinkCopy: { color: "#b7bcff", fontSize: 12, lineHeight: 18 },
  discordCode: { color: "#fff", fontSize: 15, fontWeight: "900" },
  sectionLabel: { color: "#8d8588", fontSize: 11, lineHeight: 15, fontWeight: "900" },
  sectionLabelSpaced: { marginTop: 20 },
  sectionPanelHeader: { padding: 20, paddingBottom: 10, borderTopWidth: 1, borderLeftWidth: 1, borderRightWidth: 1, borderColor: "#382326", borderTopLeftRadius: 8, borderTopRightRadius: 8, backgroundColor: "#1a090b" },
  itemGap: { height: 8 },
  sectionCard: { padding: 13, borderWidth: 1, borderColor: "#3a292c", borderRadius: 8, backgroundColor: "#160f10" },
  sectionTopline: { flexDirection: "row", alignItems: "center", gap: 10 },
  arrowColumn: { gap: 3 },
  sectionCopy: { flex: 1 },
  sectionTitle: { color: "#f2eff0", fontSize: 14, lineHeight: 19, fontWeight: "800" },
  sectionType: { marginTop: 2, color: "#7f777a", fontSize: 11, lineHeight: 15 },
  sectionActions: { marginTop: 10, flexDirection: "row", gap: 7 },
  iconButton: { width: 30, height: 22, borderWidth: 1, borderColor: "#403438", borderRadius: 5, alignItems: "center", justifyContent: "center", backgroundColor: "#21191b" },
  iconButtonDanger: { borderColor: "rgba(229,9,20,0.38)", backgroundColor: "rgba(229,9,20,0.1)" },
  iconButtonActive: { borderColor: "#777", backgroundColor: "#312729" },
  textAction: { minHeight: 34, paddingHorizontal: 11, borderWidth: 1, borderColor: "#403438", borderRadius: 7, alignItems: "center", justifyContent: "center", backgroundColor: "#21191b" },
  textActionDanger: { borderColor: "rgba(229,9,20,0.38)", backgroundColor: "rgba(229,9,20,0.1)" },
  textActionLabel: { color: "#ddd", fontSize: 12, lineHeight: 17, fontWeight: "800" },
  removeLabel: { color: "#ff626a", fontSize: 12, lineHeight: 17, fontWeight: "800" },
  removeActive: { borderColor: "#ff626a", backgroundColor: "rgba(229,9,20,0.2)" },
  toggle: { width: 42, height: 24, padding: 3, borderRadius: 12, justifyContent: "center", backgroundColor: "#393234" },
  toggleOn: { alignItems: "flex-end", backgroundColor: colors.brand },
  toggleActive: { borderWidth: 1, borderColor: "#fff" },
  toggleKnob: { width: 18, height: 18, borderRadius: 9, backgroundColor: "#aaa" },
  toggleKnobOn: { backgroundColor: "#fff" },
  addRow: { position: "relative", zIndex: 100, elevation: 100, flexDirection: "row", gap: 10 },
  addRowCompact: { flexDirection: "column" },
  selectWrap: { flex: 1, position: "relative", alignSelf: "stretch", gap: 6, zIndex: 20 },
  selectWrapExpanded: { zIndex: 200, elevation: 200 },
  selectButton: { flex: 1, minHeight: 44, paddingHorizontal: 12, borderWidth: 1, borderColor: "#47383b", borderRadius: 8, flexDirection: "row", alignItems: "center", justifyContent: "space-between", backgroundColor: "#191314" },
  selectText: { flex: 1, color: "#eee", fontSize: 14, fontWeight: "700" },
  selectChevron: { color: "#888", fontSize: 18 },
  selectChevronOpen: { transform: [{ rotate: "180deg" }] },
  selectMenu: { position: "absolute", top: 50, left: 0, right: 0, zIndex: 100, elevation: 100, padding: 5, gap: 2, borderWidth: 1, borderColor: "#47383b", borderRadius: 8, backgroundColor: "#110d0e", shadowColor: "#000", shadowOpacity: 0.45, shadowRadius: 14, shadowOffset: { width: 0, height: 6 } },
  selectOption: { minHeight: 42, paddingHorizontal: 11, borderRadius: 6, flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 10 },
  selectOptionSelected: { backgroundColor: "#3c0d10" },
  selectOptionActive: { backgroundColor: "#302628" },
  selectOptionText: { color: "#bcb5b8", fontSize: 14, lineHeight: 19, fontWeight: "700" },
  selectOptionTextSelected: { color: "#fff" },
  selectCheck: { color: colors.brand, fontSize: 16, lineHeight: 19, fontWeight: "900" },
  primaryButton: { minHeight: 44, paddingHorizontal: 16, borderRadius: 8, flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 7, backgroundColor: colors.brand },
  primaryButtonActive: { backgroundColor: "#ff1622" },
  primaryButtonText: { color: "#fff", fontSize: 13, fontWeight: "800" },
  settingRow: { minHeight: 50, paddingVertical: 8, flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 16, borderBottomWidth: 1, borderBottomColor: "#34272a" },
  settingRowExpanded: { position: "relative", zIndex: 1000, elevation: 1000 },
  settingCopy: { flex: 1 },
  settingLabel: { color: "#eee9eb", fontSize: 14, lineHeight: 19, fontWeight: "700" },
  settingDescription: { marginTop: 3, color: "#847c80", fontSize: 11, lineHeight: 15 },
  miniGrid: { flexDirection: "row", gap: 8 },
  miniGridCompact: { flexDirection: "column" },
  signOut: { minHeight: 40, paddingHorizontal: 14, borderRadius: 8, alignSelf: "flex-start", flexDirection: "row", alignItems: "center", gap: 8, backgroundColor: colors.brand },
  signOutActive: { backgroundColor: "#ff1622" },
  signOutText: { color: "#fff", fontSize: 13, fontWeight: "800" },
  accountAction: { alignSelf: "flex-start", flexDirection: "row", gap: 8 },
  routeActive: { color: "#a9a2a5", fontSize: 13, lineHeight: 18 },
  routeStrong: { color: "#fff", fontWeight: "800" },
  neutralButton: { minHeight: 44, paddingHorizontal: 12, borderWidth: 1, borderColor: "#46393c", borderRadius: 8, alignItems: "center", justifyContent: "center", backgroundColor: "#21191b" },
  neutralButtonText: { color: "#f0edef", fontSize: 13, lineHeight: 18, fontWeight: "800" },
  controlActive: { borderColor: "#82777b", backgroundColor: "#302628" },
  mutedCopy: { color: "#80787b", fontSize: 12, lineHeight: 18 },
  errorCopy: { color: "#ff626a", fontSize: 12, lineHeight: 18 },
  successCopy: { marginTop: 12, color: "#71d58a", fontSize: 12, lineHeight: 18 },
  routeResult: { minHeight: 52, padding: 10, borderWidth: 1, borderColor: "#3d3033", borderRadius: 7, flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: 12, backgroundColor: "#160f10" },
  routeResultTitle: { color: "#f2eff0", fontSize: 13, lineHeight: 18, fontWeight: "800" },
  routeLatency: { color: "#fff", fontSize: 13, lineHeight: 18, fontWeight: "800" },
  compactChoice: { minWidth: 112, minHeight: 38, paddingHorizontal: 11, borderWidth: 1, borderColor: "#47383b", borderRadius: 8, alignItems: "center", justifyContent: "center", backgroundColor: "#191314" },
  choiceText: { color: "#fff", fontSize: 12, lineHeight: 17, fontWeight: "800" },
  controlGroup: { marginTop: 10, gap: 5 },
  numberButton: { width: 76, minHeight: 38, paddingHorizontal: 12, borderWidth: 1, borderColor: "#47383b", borderRadius: 8, alignItems: "flex-end", justifyContent: "center", backgroundColor: "#191314" },
  numberText: { color: "#fff", fontSize: 14, fontWeight: "800" },
  resetButton: { alignSelf: "stretch" },
  modalScrim: { flex: 1, padding: 18, alignItems: "center", justifyContent: "center", backgroundColor: "rgba(0,0,0,0.82)" },
  modalCard: { padding: 22, borderWidth: 1, borderColor: "#49383c", borderRadius: 10, backgroundColor: "#170f11" },
  modalTitle: { marginTop: 4, color: "#fff", fontSize: 24, lineHeight: 30, fontWeight: "900" },
  fieldLabel: { marginTop: 20, marginBottom: 7, color: "#8d8588", fontSize: 11, lineHeight: 15, fontWeight: "900" },
  textInput: { minHeight: 44, paddingHorizontal: 12, borderWidth: 1, borderColor: "#47383b", borderRadius: 8, color: "#fff", fontSize: 14, fontWeight: "700", backgroundColor: "#191314" },
  jsonInput: { minHeight: 180, marginTop: 18, paddingVertical: 12, verticalAlign: "top", fontFamily: "monospace" },
  modalActions: { marginTop: 18, flexDirection: "row", justifyContent: "flex-end", gap: 10 },
  modalButton: { minWidth: 118 },
  pressed: { opacity: 0.76 },
});
